import React, { useState, useEffect, useCallback } from "react";
import "./leave.css";
import { toast } from "react-toastify";
import { applyLeave, getMyLeaves } from "../../service/project_service";
import { getProfile } from "../../service/auth_service";
import DataTable from "../../components/custom_data_table";
import CustomLoadingindicator from "../../components/custom_loading";
import { CustomSearchableSelect } from "../../components/admin/AdminFields";

const Leave = () => {
  const [formData, setFormData] = useState({
    employeename: "",
    leaveType: "",
    duration: "Full Day", // New field: Full Day or Half Day
    startDate: "",
    endDate: "",
    totalDays: "",
    reason: "",
  });

  const [leaves, setLeaves] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [profileRes, leavesRes] = await Promise.all([
        getProfile(),
        getMyLeaves(),
      ]);

      if (profileRes?.status === "success") {
        setFormData((prev) => ({
          ...prev,
          employeename: profileRes.data?.employeename || "",
        }));
      }

      if (leavesRes?.status === "success") {
        const reports = leavesRes.data.leaves || [];
        const formattedLeaves = reports.map((leave, index) => ({
          ...leave,
          sno: index + 1,
          displayLeaveType: leave.leavetype || leave.leave_type || "-",
          displayStartDate: leave.startdate || leave.start_date || "-",
          displayEndDate: leave.enddate || leave.end_date || "-",
          displayTotalDays: leave.totaldays || leave.total_days || 0,
        }));
        setLeaves(formattedLeaves);
        setSummary(leavesRes.data.summary);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to fetch initial data");
    } finally {
      setLoading(false);
    }
  };

  const { startDate, endDate, duration } = formData;

  const calculateDays = useCallback(() => {
    if (!startDate || (duration === "Full Day" && !endDate)) {
      setFormData((prev) => ({ ...prev, totalDays: "" }));
      return;
    }

    if (duration.includes("Half Day")) {
      setFormData((prev) => ({ ...prev, totalDays: "0.5", endDate: startDate }));
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
      setFormData((prev) => ({ ...prev, totalDays: "0" }));
      return;
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    setFormData((prev) => ({ ...prev, totalDays: diffDays.toString() }));
  }, [startDate, endDate, duration]);

  useEffect(() => {
    calculateDays();
  }, [calculateDays]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error("End date cannot be before start date");
      return;
    }

    try {
      setSubmitLoading(true);
      const payload = {
        leavetype: formData.leaveType,
        duration: formData.duration,
        startdate: formData.startDate,
        enddate: formData.endDate,
        totaldays: Number(formData.totalDays),
        reason: formData.reason,
      };

      const response = await applyLeave(payload);

      if (response.status === "success") {
        toast.success(response.message || "Leave applied successfully");
        setFormData((prev) => ({
          ...prev,
          leaveType: "",
          duration: "Full Day",
          startDate: "",
          endDate: "",
          totalDays: "",
          reason: "",
        }));
        const leavesRes = await getMyLeaves();
        if (leavesRes?.status === "success") {
          const reports = leavesRes.data.leaves || [];
          const formattedLeaves = reports.map((leave, index) => ({
            ...leave,
            sno: index + 1,
            displayLeaveType: leave.leavetype || leave.leave_type || "-",
            displayStartDate: leave.startdate || leave.start_date || "-",
            displayEndDate: leave.enddate || leave.end_date || "-",
            displayTotalDays: leave.totaldays || leave.total_days || 0,
          }));
          setLeaves(formattedLeaves);
          setSummary(leavesRes.data.summary);
        }
      } else {
        toast.error(response.message || "Failed to apply leave");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  const leaveColumns = [
    {
      key: "sno",
      label: "S.No",
    },
    {
      key: "employeename",
      label: "Name",
    },
    {
      key: "displayStartDate",
      label: "Start Date",
      render: (value) =>
        value && value !== "-"
          ? new Date(value).toLocaleDateString("en-GB")
          : "-",
    },
    {
      key: "displayEndDate",
      label: "End Date",
      render: (value) =>
        value && value !== "-"
          ? new Date(value).toLocaleDateString("en-GB")
          : "-",
    },
    {
      key: "displayTotalDays",
      label: "Total Days",
    },
    {
      key: "reason",
      label: "Reason",
    },
    {
      key: "status",
      label: "Status",
      render: (value) => (
        <span
          style={{
            color:
              value === "Approved"
                ? "#2e7d32"
                : value === "Rejected"
                  ? "#d32f2f"
                  : "#ed6c02",
            fontWeight: "600",
            padding: "4px 8px",
            borderRadius: "4px",
            backgroundColor:
              value === "Approved"
                ? "#e8f5e9"
                : value === "Rejected"
                  ? "#ffeeeb"
                  : "#fff3e0",
            fontSize: "12px",
          }}
        >
          {value || "Pending"}
        </span>
      ),
    },
  ];

  if (loading) {
    return <CustomLoadingindicator />;
  }

  return (
    <div className="leave-container">
      {summary && (
        <div className="leave-summary-header">
          <div className="summary-card total">
            <div className="summary-info">
              <span className="summary-label">Total (YTD / Year)</span>
              <span className="summary-value">{summary.eligibleLeaves} / {summary.yearlyTotal}</span>
            </div>
          </div>
          <div className="summary-card taken">
            <div className="summary-info">
              <span className="summary-label">Leaves Taken</span>
              <span className="summary-value">{summary.takenLeaves}</span>
            </div>
          </div>
          <div className="summary-card balance">
            <div className="summary-info">
              <span className="summary-label">Available Balance</span>
              <span className="summary-value">{summary.balanceLeaves}</span>
            </div>
          </div>
          <div className="summary-card cl">
            <div className="summary-info">
              <span className="summary-label">Casual (Available / Total)</span>
              <span className="summary-value">{summary.casualLeave.balance} / {summary.casualLeave.yearlyTotal}</span>
            </div>
          </div>
          <div className="summary-card sl">
            <div className="summary-info">
              <span className="summary-label">Sick (Available / Total)</span>
              <span className="summary-value">{summary.sickLeave.balance} / {summary.sickLeave.yearlyTotal}</span>
            </div>
          </div>
        </div>
      )}

      <h4>Apply Leave</h4>
      <div className="leave-card">
        <form className="leave-form" onSubmit={handleSubmit}>
          <div className="leave-form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.employeename}
              readOnly
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </div>

          <div className="leave-form-group">
            <CustomSearchableSelect
              label="Leave Type *"
              placeholder="Select Leave Type"
              value={formData.leaveType}
              onChange={(e) => setFormData(prev => ({ ...prev, leaveType: e.target.value }))}
              options={[
                { label: "Casual Leave", value: "Casual Leave" },
                { label: "Sick Leave", value: "Sick Leave" },
                { label: "Loss of Pay", value: "Loss of Pay" }
              ]}
            />
          </div>

          <div className="leave-form-group">
            <CustomSearchableSelect
              label="Duration *"
              placeholder="Select Duration"
              value={formData.duration}
              onChange={(e) => {
                const val = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  duration: val,
                  endDate: val.includes("Half Day") ? prev.startDate : prev.endDate
                }));
              }}
              options={[
                { label: "Full Day", value: "Full Day" },
                { label: "Half Day - Morning", value: "Half Day - Morning" },
                { label: "Half Day - Afternoon", value: "Half Day - Afternoon" }
              ]}
            />
          </div>

          <div className="leave-form-group">
            <label>Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="leave-form-group">
            <label>End Date *</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              required={formData.duration === "Full Day"}
              disabled={formData.duration.includes("Half Day")}
              style={{ backgroundColor: formData.duration.includes("Half Day") ? "#f5f5f5" : "inherit" }}
            />
          </div>

          <div className="leave-form-group">
            <label>Total Days</label>
            <input
              type="text"
              name="totalDays"
              value={formData.totalDays}
              readOnly
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </div>

          <div className="leave-form-group leave-form-group-full">
            <label>Reason *</label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              rows="4"
              placeholder="Enter reason for leave..."
              required
            />
          </div>

          <div className="submit-buttons leave-form-group-full">
            <button
              type="submit"
              className="apply-btn"
              disabled={submitLoading}
            >
              {submitLoading ? "Loading..." : "Submit"}
            </button>
            <button
              type="button"
              className="reset-btn"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  leaveType: "",
                  duration: "Full Day",
                  startDate: "",
                  endDate: "",
                  totalDays: "",
                  reason: "",
                }))
              }
              disabled={submitLoading}
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      <div style={{ height: 30 }}></div>

      <h4>My Leaves</h4>
      <DataTable
        columns={leaveColumns}
        data={leaves}
        loading={loading}
        pageSize={5}
      />
    </div>
  );
};

export default Leave;
